import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { papersApi, auditApi } from '../services/api';
import { Paper, AuditTask } from '../types';
import FileUpload from '../components/FileUpload';
import AuditStatusBadge from '../components/AuditStatusBadge';
import ProjectInfo from '../components/ProjectInfo';
import StatusBadge from '../components/StatusBadge';
import PaperTaskStatus from '../components/PaperTaskStatus';
import { showToast } from '../components/Toast';

function Papers() {
  const navigate = useNavigate();
  const [papers, setPapers] = useState<Paper[]>([]);
  const [paperTasks, setPaperTasks] = useState<Record<string, AuditTask[]>>({});
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [error, setError] = useState('');
  // Per-paper audit type selection
  const [paperAuditTypes, setPaperAuditTypes] = useState<Record<string, string>>({});

  const fetchPapers = async () => {
    try {
      const response = await papersApi.list();
      const papersData = response.data.items;
      setPapers(papersData);

      // Fetch tasks for each paper to show status
      const tasksMap: Record<string, AuditTask[]> = {};
      await Promise.all(
        papersData.map(async (paper: Paper) => {
          try {
            const tasksRes = await auditApi.listTasks(paper.id);
            if (tasksRes.data && tasksRes.data.length > 0) {
              tasksMap[paper.id] = tasksRes.data;
            }
          } catch {
            // Ignore errors for papers without tasks
          }
        })
      );
      setPaperTasks(tasksMap);
    } catch (err: any) {
      setError('获取论文列表失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPapers();
  }, []);

  const handleUpload = async (file: File) => {
    setUploading(true);
    setUploadProgress(0);
    setError('');

    try {
      await papersApi.upload(file, undefined, undefined, (percent) => {
        setUploadProgress(percent);
      });
      showToast('论文上传成功', 'success');
      await fetchPapers();
    } catch (err: any) {
      let msg = err.response?.data?.detail || '上传失败';
      if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        msg = '上传超时，请检查网络或稍后重试';
      } else if (err.message?.includes('Network Error')) {
        msg = '网络错误，文件可能已上传成功，请刷新页面查看';
      }
      setError(msg);
      showToast(msg, 'error');
    } finally {
      // Always refresh list and reset UI, even if upload request timed out
      // The file may have been successfully saved on the server
      await fetchPapers();
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm('确定要删除这篇论文吗？')) return;

    try {
      await papersApi.delete(id);
      showToast('论文已删除', 'success');
      await fetchPapers();
    } catch (err: any) {
      setError('删除失败');
      showToast('删除失败', 'error');
    }
  };

  const getAuditType = (paperId: string) => paperAuditTypes[paperId] || 'full_audit';

  const setAuditType = (paperId: string, type: string) => {
    setPaperAuditTypes((prev) => ({ ...prev, [paperId]: type }));
  };

  const handleAudit = async (paperId: string) => {
    const auditType = getAuditType(paperId);
    try {
      await auditApi.createTask(paperId, auditType);
      showToast('审查任务已创建', 'success');
      // Refresh tasks for this paper
      const tasksRes = await auditApi.listTasks(paperId);
      setPaperTasks((prev) => ({ ...prev, [paperId]: tasksRes.data }));
    } catch (err: any) {
      const msg = err.response?.data?.detail || '创建审查任务失败';
      setError(msg);
      showToast(msg, 'error');
    }
  };

  const handleViewLatestReport = (paperId: string) => {
    const tasks = paperTasks[paperId];
    if (!tasks) return;
    const completed = tasks
      .filter((t) => t.status === 'completed')
      .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    if (completed.length > 0) {
      navigate(`/report/${completed[0].id}`);
    }
  };

  const auditTypes = [
    { value: 'full_audit', label: '完整审查' },
    { value: 'numeric_forensics', label: '数值取证' },
    { value: 'image_integrity', label: '图像完整性' },
    { value: 'citation_check', label: '引用检查' },
    { value: 'package_completeness', label: '包完整性' },
    { value: 'geng_numeric', label: 'Geng数值筛查' },
    { value: 'flow_plot', label: '流式图检测' },
  ];

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-6">
      {/* Project Info */}
      <ProjectInfo />

      {/* Upload Section */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">
          上传论文
        </h2>
        <FileUpload onUpload={handleUpload} isUploading={uploading} uploadProgress={uploadProgress} />
      </div>

      {/* Error */}
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-md">
          <p className="text-sm text-red-600">{error}</p>
        </div>
      )}

      {/* Papers List */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            我的论文 ({papers.length})
          </h2>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-500">加载中...</div>
        ) : papers.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            暂无论文，请上传
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {papers.map((paper) => (
              <div
                key={paper.id}
                className="px-6 py-4 hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {paper.title || paper.original_filename}
                    </p>
                    <p className="text-sm text-gray-500">
                      {paper.doi && `DOI: ${paper.doi} | `}
                      {formatFileSize(paper.file_size)} |{' '}
                      {new Date(paper.created_at).toLocaleString()}
                    </p>
                  </div>

                  <div className="flex items-center space-x-3 ml-4">
                    <AuditStatusBadge paper={paper} />
                    <StatusBadge status={paper.upload_status} />

                    {/* Show task status if exists */}
                    {paperTasks[paper.id] && paperTasks[paper.id].length > 0 && (
                      <button
                        onClick={() => handleViewLatestReport(paper.id)}
                        className="hover:opacity-80"
                        title="查看最新报告"
                      >
                        <PaperTaskStatus tasks={paperTasks[paper.id]} />
                      </button>
                    )}

                    <select
                      value={getAuditType(paper.id)}
                      onChange={(e) => setAuditType(paper.id, e.target.value)}
                      className="px-2 py-1 text-sm border border-gray-300 rounded-md"
                    >
                      {auditTypes.map((t) => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                    <button
                      onClick={() => handleAudit(paper.id)}
                      className="px-3 py-1 text-sm font-medium text-blue-600 bg-blue-50 rounded-md hover:bg-blue-100"
                    >
                      审查
                    </button>

                    <button
                      onClick={() => handleDelete(paper.id)}
                      className="px-3 py-1 text-sm font-medium text-red-600 bg-red-50 rounded-md hover:bg-red-100"
                    >
                      删除
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default Papers;
